/**
 * The canonical glyph for each action in the app.
 *
 * Every labelled action button renders its icon from here — never straight from
 * "lucide-react". The point is not convenience, it is that "Save" must be the
 * same glyph in the vehicle modal, the settings pane and the template editor.
 * Before this file existed, Delete was a trash can in the row menus and bare
 * text in the modal footers, Edit was `Pencil` in some files and radix's
 * `Pencil2Icon` in others, and icon sizes ranged over 12, 14, 15, 16 and 18.
 *
 * House rules (also in docs/MODAL_STYLE.md):
 *   - Buttons draw their icon at ICON_SIZE (16). Icon-only controls sitting in a
 *     table row draw at ROW_ICON_SIZE (14) — they are in a 32px row, not a 32px
 *     button, and 16 crowds them.
 *   - Icon first, label second.
 *   - An icon-only button needs an aria-label or a title. No exceptions.
 *
 * If an action you need is not here, add it here rather than importing lucide
 * directly — that is how the drift started last time.
 */
export {
  Save as SaveIcon,
  X as CancelIcon,
  Trash2 as DeleteIcon,
  Plus as AddIcon,
  Pencil as EditIcon,
  Download as ExportIcon,
  Printer as PrintIcon,
  Send as SendIcon,
  Search as SearchIcon,
  RefreshCw as RefreshIcon,
  Check as ConfirmIcon,
  ArrowLeft as BackIcon,
  ArrowRight as NextIcon,
  Upload as UploadIcon,
  Copy as CopyIcon,
  LogOut as SignOutIcon,
  Settings as SettingsIcon,
  Eye as ViewIcon,
  HelpCircle as HelpIcon,
  // Domain actions. "Cancel a deal" is destructive but is not a delete, so it
  // must not borrow the trash can — that would read as "remove this record".
  Ban as VoidIcon,
  Truck as DeliverIcon,
  BadgeCheck as ClearedIcon,
  Handshake as SellIcon,
  Undo2 as ReopenIcon,
  Filter as FilterIcon,
  LogIn as SignInIcon,
  Sparkles as AiIcon,
  Fingerprint as PasskeyIcon,
  EyeOff as HideIcon,
  // Status glyphs, kept here so the radix-icons set had somewhere to land when
  // it was retired. Radix icons default to 15px and lucide's default to 24px,
  // so every one of these must be given an explicit size at the call site.
  AlertTriangle as WarningIcon,
  Info as InfoIcon,
  Mail as EmailIcon,
  CheckCircle2 as SuccessIcon,
  File as FileIcon,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";

/** Buttons. */
export const ICON_SIZE = 16;
/** Icon-only controls inside a table row. */
export const ROW_ICON_SIZE = 14;
